import { Entity, FunctionCollection, RauriRandom } from "~/utils";

export type Choice<
  Callbacks extends FunctionCollection,
  Key extends keyof Callbacks = keyof Callbacks
> = Entity & {
  playerId: string;
  type: Key;
  possibleChoices: Parameters<Callbacks[Key]>[0][];
  choiceCount?: number;
  optional?: boolean;
  results: ChoiceResult<Callbacks, Key>[];
};

export type NewChoice<
  Callbacks extends FunctionCollection,
  Key extends keyof Callbacks = keyof Callbacks
> = Omit<Choice<Callbacks, Key>, "id" | "results">;

export const Choice = {
  new<Callbacks extends FunctionCollection, Key extends keyof Callbacks>(
    choice: NewChoice<Callbacks, Key>
  ): Choice<Callbacks, Key> {
    const id = RauriRandom.sha256ToString(JSON.stringify(choice));
    return { ...choice, id, results: [] };
  },
  isAnswered(choice: Choice<any, any>) {
    const { choiceCount = 1, results } = choice;
    const firstResult = results.at(0);
    if (!firstResult) {
      return false;
    }
    if (results.some((result) => result.skip)) {
      return true;
    }
    return results.length === choiceCount;
  },
  getChoiceResultArg<
    Callbacks extends FunctionCollection,
    Key extends keyof Callbacks
  >(choiceResult: ChoiceResult<Callbacks, Key>) {
    const { skip } = choiceResult;
    if (skip) return undefined;
    const { value } = choiceResult;
    return value;
  },
};

export type ChoiceResult<
  Callbacks extends FunctionCollection,
  Key extends keyof Callbacks = keyof Callbacks
> =
  | {
      skip: true;
    }
  | {
      skip: false;
      value: Parameters<Callbacks[Key]>[0];
    };
